import { supabase } from '../lib/supabase';

// Pipeline Service for job-specific candidate management
class PipelineService {
  // Get all jobs for pipeline selection
  async getJobs() {
    try {
      const { data, error } = await supabase?.from('jobs')?.select(`
          id,
          title,
          department,
          status,
          employment_type,
          location,
          created_at
        `)?.eq('status', 'open')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      return [];
    }
  }

  // Get applications with candidate data for specific job
  async getApplicationsForJob(jobId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select(`
          id,
          stage,
          score,
          created_at,
          candidate_id,
          candidates!inner (
            id,
            full_name,
            email,
            phone,
            location,
            linkedin,
            source,
            resume_url,
            created_at
          )
        `)?.eq('job_id', jobId)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Transform data to match UI expectations
      return (data || [])?.map(app => ({
        id: app?.id,
        candidateId: app?.candidate_id,
        name: app?.candidates?.full_name || 'Unknown Candidate',
        email: app?.candidates?.email || '',
        phone: app?.candidates?.phone || '',
        location: app?.candidates?.location || '',
        linkedin: app?.candidates?.linkedin || '',
        source: app?.candidates?.source || 'Direct',
        stage: app?.stage || 'new',
        score: app?.score ? Number(app?.score) : null,
        appliedDate: app?.created_at,
        resumeUrl: app?.candidates?.resume_url || null,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(app?.candidates?.full_name || 'User')}&background=6366f1&color=fff`,
        avatarAlt: `Professional headshot of ${app?.candidates?.full_name || 'candidate'}`
      }));
    } catch (error) {
      console.error('Error fetching applications:', error);
      return [];
    }
  }

  // Get notes for specific application
  async getNotesForApplication(applicationId) {
    try {
      const { data, error } = await supabase?.from('notes')?.select(`
          id,
          body,
          created_at,
          author_id,
          users!inner (
            id,
            email
          )
        `)?.eq('application_id', applicationId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])?.map(note => ({
        id: note?.id,
        content: note?.body,
        author: note?.users?.email?.split('@')?.[0] || 'Unknown',
        timestamp: note?.created_at
      }));
    } catch (error) {
      console.error('Error fetching notes:', error);
      return [];
    }
  }

  // Get events for specific application
  async getEventsForApplication(applicationId) {
    try {
      const { data, error } = await supabase?.from('events')?.select(`
          id,
          type,
          payload,
          created_at,
          actor_id,
          users (
            id,
            email
          )
        `)?.eq('application_id', applicationId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return (data || [])?.map(event => ({
        id: event?.id,
        action: event?.type || 'activity',
        timestamp: event?.created_at,
        actor: event?.users?.email?.split('@')?.[0] || 'System',
        details: event?.payload || {}
      }));
    } catch (error) {
      console.error('Error fetching events:', error);
      return [];
    }
  }

  // Move candidate to different stage
  async moveCandidateToStage(applicationId, newStage) {
    try {
      const { data, error } = await supabase?.from('applications')?.update({ stage: newStage })?.eq('id', applicationId)?.select();

      if (error) throw error;

      // Log the stage change event
      await this.logEvent(applicationId, 'stage_change', {
        new_stage: newStage,
        timestamp: new Date()?.toISOString()
      });

      return data?.[0] || null;
    } catch (error) {
      console.error('Error moving candidate:', error);
      throw error;
    }
  }

  // Update candidate score
  async updateCandidateScore(applicationId, score) {
    try {
      const { data, error } = await supabase?.from('applications')?.update({ score })?.eq('id', applicationId)?.select();

      if (error) throw error;

      return data?.[0] || null;
    } catch (error) {
      console.error('Error updating score:', error);
      throw error;
    }
  }

  // Add note to application
  async addNote(applicationId, noteText) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('notes')?.insert({
          application_id: applicationId,
          body: noteText,
          author_id: user?.id,
          org_id: user?.user_metadata?.org_id
        })?.select();

      if (error) throw error;

      return data?.[0] || null;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  }

  // Log event for application
  async logEvent(applicationId, eventType, payload = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      
      const { data, error } = await supabase?.from('events')?.insert({
          application_id: applicationId,
          type: eventType,
          payload,
          actor_id: user?.id || null,
          org_id: user?.user_metadata?.org_id
        })?.select();

      if (error) throw error;
      return data?.[0] || null;
    } catch (error) {
      console.error('Error logging event:', error);
      return null;
    }
  }

  // Get pipeline analytics for specific job
  async getJobPipelineAnalytics(jobId) {
    try {
      const { data, error } = await supabase?.from('applications')?.select('stage, score, created_at')?.eq('job_id', jobId);

      if (error) throw error;

      const applications = data || [];
      const stages = ['new', 'screening', 'interview', 'final-review', 'offer', 'hired', 'rejected'];
      
      // Calculate stage distribution
      const stageDistribution = stages?.reduce((acc, stage) => {
        acc[stage] = applications?.filter(app => app?.stage === stage)?.length;
        return acc;
      }, {});

      // Calculate average time to hire (mock calculation)
      const hiredApps = applications?.filter(app => app?.stage === 'hired');
      const averageTimeToHire = hiredApps?.length > 0 ? 
        Math.round(hiredApps?.reduce((acc, app) => {
          const daysSinceApplication = Math.floor((new Date() - new Date(app.created_at)) / (1000 * 60 * 60 * 24));
          return acc + daysSinceApplication;
        }, 0) / hiredApps?.length) : 0;

      // Calculate conversion rates
      const totalApplications = applications?.length;
      const conversionRates = {
        'new_to_screening': totalApplications > 0 ? (stageDistribution?.screening + stageDistribution?.interview + stageDistribution?.['final-review'] + stageDistribution?.offer + stageDistribution?.hired) / totalApplications * 100 : 0,
        'screening_to_interview': stageDistribution?.screening > 0 ? (stageDistribution?.interview + stageDistribution?.['final-review'] + stageDistribution?.offer + stageDistribution?.hired) / (stageDistribution?.screening + stageDistribution?.interview + stageDistribution?.['final-review'] + stageDistribution?.offer + stageDistribution?.hired) * 100 : 0,
        'interview_to_offer': (stageDistribution?.interview + stageDistribution?.['final-review']) > 0 ? (stageDistribution?.offer + stageDistribution?.hired) / (stageDistribution?.interview + stageDistribution?.['final-review'] + stageDistribution?.offer + stageDistribution?.hired) * 100 : 0,
        'offer_to_hire': stageDistribution?.offer > 0 ? stageDistribution?.hired / (stageDistribution?.offer + stageDistribution?.hired) * 100 : 0
      };

      return {
        totalApplications,
        stageDistribution,
        averageTimeToHire,
        conversionRates,
        averageScore: applications?.filter(app => app?.score)?.reduce((acc, app) => acc + Number(app?.score), 0) / applications?.filter(app => app?.score)?.length || 0
      };
    } catch (error) {
      console.error('Error getting analytics:', error);
      return {
        totalApplications: 0,
        stageDistribution: {},
        averageTimeToHire: 0,
        conversionRates: {},
        averageScore: 0
      };
    }
  }

  // Bulk operations
  async bulkMoveApplications(applicationIds, newStage) {
    try {
      const { data, error } = await supabase?.from('applications')?.update({ stage: newStage })?.in('id', applicationIds)?.select();

      if (error) throw error;

      // Log bulk stage change events
      for (const appId of applicationIds) {
        await this.logEvent(appId, 'bulk_stage_change', {
          new_stage: newStage,
          bulk_operation: true,
          timestamp: new Date()?.toISOString()
        });
      }

      return data || [];
    } catch (error) {
      console.error('Error bulk moving applications:', error);
      throw error;
    }
  }

  async bulkRejectApplications(applicationIds) {
    return this.bulkMoveApplications(applicationIds, 'rejected');
  }
}

export const pipelineService = new PipelineService();
export default pipelineService;